import moment from "moment";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { DateTime } from "@/Common/Helpers/DateTime";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { VisibilityBasedInterval } from "@/Common/Helpers/VisibilityBasedInterval";
import { MyleStaticResources } from "@/Common/Localization/MyleStaticResouces";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { EnumPortalSyncStatus, MyPreventiveActionsSortOrderType, PopulationBasedFundingStatus, PortfolioConsultationRequestsSortOrderType, PortfolioExamRequestSortOrderType, PortfolioInboxes, PortfolioNotesSortOrderType, PortfolioPortalSyncHistoryOrderType, PortfolioSentFaxSortOrderType, PortfolioSentFaxStatus, PreventiveRecallTab, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { IndeterminateCheckboxState } from "@/Common/Models/UI/Inputs/IndeterminateCheckboxState";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import { PreventiveActivitiesSettingsContainerState } from "@/Portfolio/Components/PreventiveActivitiesSettingsContainer";
import { PortfolioHelper } from "@/Portfolio/Helpers/PortfolioHelper";
import { PopulationBasedFundingPortfolioModel } from "@/Portfolio/Models/PopulationBasedFunding/PopulationBasedFundingPortfolioModel";
import { PopulationBasedFundingStatusUIModel } from "@/Portfolio/Models/PopulationBasedFunding/PopulationBasedFundingStatusUIModel";
import { MedicalRequestsInboxSettings } from "@/Portfolio/Models/Settings/MedicalRequestsInboxSettings";
import { PopulationBasedFundingPortfolioFilterSettings } from "@/Portfolio/Models/Settings/PopulationBasedFundingTableFilterSettings";
import { ApssFilterType, IAPSSInboxModel } from "@/Portfolio/Models/UI/IAPSSInboxModel";
import { IConsultationRequestInboxModel } from "@/Portfolio/Models/UI/IConsultationRequestsInboxModel";
import { IConsultationRequestPopupDataModel } from "@/Portfolio/Models/UI/IConsultationRequestsPopupDataModel";
import { INotesInboxModel } from "@/Portfolio/Models/UI/INotesInboxModel";
import { IPendingApprovalInboxModel } from "@/Portfolio/Models/UI/IPendingApprovalInboxModel";
import { IPortalSyncModel } from "@/Portfolio/Models/UI/IPortalSyncModel";
import { IPortfolioTabModel } from "@/Portfolio/Models/UI/IPortfolioTabModel";
import { IPreventiveActivitiesInboxModel } from "@/Portfolio/Models/UI/IPreventiveActivitiesInboxModel";
import { ILabRequestPopupDataModel } from "@/Portfolio/Models/UI/IRequestPopupDataModel";
import { ILabRequestsInboxModel } from "@/Portfolio/Models/UI/IRequestsInboxModel";
import { ISentFaxInboxModel } from "@/Portfolio/Models/UI/ISentFaxInboxModel";
import { PortfolioAction } from "@/Portfolio/Stores/Actions/PortfolioAction";
import { ConsultationRequestInboxHandler } from "@/Portfolio/Stores/Handlers/Local/MyConsultationRequestHandler";
import { PortfolioResponseHandler } from "@/Portfolio/Stores/Handlers/Server/PortfolioResponseHandler";
import { PreventiveRecallListSettingsContainerState } from "../Components/PreventiveRecallListSettingsContainer";
import { MedicalRequestStatus } from "../Models/MedicalRequestStatus";
import { IPreventiveRecallListModel } from "../Models/UI/IPreventiveRecallListModel";
import { MyConsultationRequestsSettingsContainerHandler } from "./Handlers/Local/MyConsultationRequestsSettingsContainerHandler";
import { NotesInboxHandler } from "./Handlers/Local/MyNotesInboxHandler";
import { MyNotesSettingsContainerHandler } from "./Handlers/Local/MyNotesSettingsContainerHandler";
import { RequestsInboxHandler } from "./Handlers/Local/MyRequestsInboxHandler";
import { MyRequestsSettingsContainerHandler } from "./Handlers/Local/MyRequestsSettingsContainerHandler";
import { SentFaxInboxHandler } from "./Handlers/Local/MySentFaxInboxHandler";
import { MySentFaxSettingsContainerHandler } from "./Handlers/Local/MySentFaxSettingsContainerHandler";
import { PendingApprovalInboxHandler } from "./Handlers/Local/PendingApprovalInboxHandler";
import { PendingApprovalSettingsContainerHandler } from "./Handlers/Local/PendingApprovalSettingsContainerHandler";
import { PopulationBasedFundingHandler } from "./Handlers/Local/PopulationBasedFundingHandler";
import { PortalSyncHandler } from "./Handlers/Local/PortalSyncHandler";
import { PortfolioApssInboxHandler } from "./Handlers/Local/PortfolioApssInboxHandler";
import { PortfolioTabHandler } from "./Handlers/Local/PortfolioTabHandler";
import { PreventiveActivitiesInboxHandler } from "./Handlers/Local/PreventiveActivitiesInboxHandler";
import { PreventiveRecallListHandler } from "./Handlers/Local/PreventiveRecallListHandler";
import { PreventiveRecallListSettingsContainerHandler } from "./Handlers/Local/PreventiveRecallListSettingsContainerHandler";

let defaultInstance: PortfolioStore;

class PortfolioStore extends BaseStore {
    // mappers
    private mapper: RequestMapper;
    private serverMapper: ServerResponseMapper;

    // handlers server events
    private portfolioTabHandler: PortfolioTabHandler;
    private NotesInboxHandler: NotesInboxHandler;
    private consultationRequestsInboxHandler: ConsultationRequestInboxHandler;
    private requestsInboxHandler: RequestsInboxHandler;
    private myNotesSettingsContainerHandler: MyNotesSettingsContainerHandler;
    private myConsultationRequestsSettingsContainerHandler: MyConsultationRequestsSettingsContainerHandler;
    private myRequestsSettingsContainerHandler: MyRequestsSettingsContainerHandler;
    private sentFaxInboxHandler: SentFaxInboxHandler;
    private mySentFaxSettingsContainerHandler: MySentFaxSettingsContainerHandler;
    private portfolioAPSSInboxHandler: PortfolioApssInboxHandler;
    private pendingApprovalInboxHandler: PendingApprovalInboxHandler;
    private pendingApprovalSettingsContainerHandler: PendingApprovalSettingsContainerHandler;
    private portalSyncHandler: PortalSyncHandler;
    private preventiveActivitiesInboxHandler: PreventiveActivitiesInboxHandler;
    private preventiveRecallListHandler: PreventiveRecallListHandler;
    private preventiveRecallListSettingsContainerHandler: PreventiveRecallListSettingsContainerHandler;
    private responseHandler: PortfolioResponseHandler;

    private selectedEmployeeAPSS = sessionStore.getAPSSEmployee();

    public initialLoadDone: boolean;
    public Model: IPortfolioTabModel;
    public NotesInboxModel: INotesInboxModel;
    public ConsultationRequestsInboxModel: IConsultationRequestInboxModel;
    public RequestsInboxModel: ILabRequestsInboxModel;
    public ConsultationRequestPopupDataModel: IConsultationRequestPopupDataModel;
    public RequestPopupDataModel: ILabRequestPopupDataModel;

    public sentFaxInboxModel: ISentFaxInboxModel;
    public failedFaxInboxModel: ISentFaxInboxModel;

    public PendingApprovalInboxModel: IPendingApprovalInboxModel;
    public apssInboxModel: IAPSSInboxModel;
    public preventiveActivitiesInboxModel: IPreventiveActivitiesInboxModel;
    public preventiveRecallListModel: IPreventiveRecallListModel;

    public portalSyncModel: IPortalSyncModel;
    public populationBasedFundingHandler: PopulationBasedFundingHandler;

    public PortfolioActions: PortfolioAction;

    public ExpectedResponseStore: any;
    public staticResources: MyleStaticResources;

    public helper: MyleHelper;

    public timeoutReferenceAPSS;
    public timeoutReferencePreventiveRecallList: number;

    public populationBasedFundingPortfolioModel: PopulationBasedFundingPortfolioModel = new PopulationBasedFundingPortfolioModel();

    public conseilNumeriqueInterval: VisibilityBasedInterval;

    constructor() {
        super();

        this.helper = new MyleHelper();
        this.staticResources = new MyleStaticResources();
        this.timeoutReferencePreventiveRecallList = -1;

        this.PortfolioActions = new PortfolioAction();

        this.mapper = new RequestMapper();
        this.serverMapper = new ServerResponseMapper();

        this.portfolioTabHandler = new PortfolioTabHandler();
        this.NotesInboxHandler = new NotesInboxHandler();
        this.consultationRequestsInboxHandler = new ConsultationRequestInboxHandler();
        this.requestsInboxHandler = new RequestsInboxHandler();
        this.sentFaxInboxHandler = new SentFaxInboxHandler();
        this.myNotesSettingsContainerHandler = new MyNotesSettingsContainerHandler();
        this.myConsultationRequestsSettingsContainerHandler = new MyConsultationRequestsSettingsContainerHandler();
        this.myRequestsSettingsContainerHandler = new MyRequestsSettingsContainerHandler();
        this.mySentFaxSettingsContainerHandler = new MySentFaxSettingsContainerHandler();
        this.portfolioAPSSInboxHandler = new PortfolioApssInboxHandler();
        this.responseHandler = new PortfolioResponseHandler(this.PortfolioActions);
        this.pendingApprovalInboxHandler = new PendingApprovalInboxHandler();
        this.pendingApprovalSettingsContainerHandler = new PendingApprovalSettingsContainerHandler();
        this.preventiveActivitiesInboxHandler = new PreventiveActivitiesInboxHandler();
        this.preventiveRecallListHandler = new PreventiveRecallListHandler();
        this.preventiveRecallListSettingsContainerHandler = new PreventiveRecallListSettingsContainerHandler();
        this.portalSyncHandler = new PortalSyncHandler();
        this.populationBasedFundingHandler = new PopulationBasedFundingHandler();

        this.mapper.AddInstance(this.NotesInboxHandler);
        this.mapper.AddInstance(this.consultationRequestsInboxHandler);
        this.mapper.AddInstance(this.requestsInboxHandler);
        this.mapper.AddInstance(this.sentFaxInboxHandler);
        this.mapper.AddInstance(this.myNotesSettingsContainerHandler);
        this.mapper.AddInstance(this.portfolioTabHandler);
        this.mapper.AddInstance(this.myConsultationRequestsSettingsContainerHandler);
        this.mapper.AddInstance(this.myRequestsSettingsContainerHandler);
        this.mapper.AddInstance(this.mySentFaxSettingsContainerHandler);
        this.mapper.AddInstance(this.portfolioAPSSInboxHandler);
        this.mapper.AddInstance(this.pendingApprovalInboxHandler);
        this.mapper.AddInstance(this.pendingApprovalSettingsContainerHandler);
        this.mapper.AddInstance(this.portalSyncHandler);
        this.mapper.AddInstance(this.preventiveActivitiesInboxHandler);
        this.mapper.AddInstance(this.preventiveRecallListSettingsContainerHandler);
        this.mapper.AddInstance(this.preventiveRecallListHandler);
        this.mapper.AddInstance(this.populationBasedFundingHandler);

        this.serverMapper.AddInstance(this.responseHandler);

        const defaultModel = {
            NotesCount: 0,
            CurrentInbox: PortfolioInboxes.Notes,
            SupervisionNoteCount: 0
        };

        const defaultNotesInboxModel = {
            TableData: [],
            SelectedPageSizeOption: 50,
            PageSizeOptions: [50, 100, 150],
            SelectedPageNumber: 1,
            TotalQueryPages: 0,
            TotalQueryRows: 0,
            CurrentPage: 0,
            GlobalPortfolioSearchText: "",
            GlobalPortfolioSearchPlaceholder: "",
            ShowMyNotesDisplaySettings: false,
            SelectAllSitesChecked: false,
            SelectAllCliniciansChecked: false,
            SelectAllStatusChecked: false,
            Sites: [],
            CliniciansSearchText: "",
            CliniciansGlobalCache: [],
            CliniciansFiltered: [],
            ColumnOrderingPopupOpen: false,
            Status: [],
            OrderByDirection: SortDirection.DESC,
            OrderByColumn: PortfolioNotesSortOrderType.Date
        };

        const defaultConsultationRequestsInboxModel = {
            GlobalPortfolioSearchText: "",
            GlobalPortfolioSearchPlaceholder: "",
            TableData: [],
            Sites: [],
            SelectAllSitesChecked: false,
            SelectAllStatusChecked: false,
            SelectAllCreatedByChecked: false,
            SelectAllRequestingStaffChecked: false,
            SelectAllPriorityChecked: false,
            CreatedByGlobalCache: [],
            CreatedByCliniciansSearchText: "",
            CreatedByClinicianList: [],
            RequestingStaffGlobalCache: [],
            RequestingStaffList: [],
            RequestingStaffsSearchText: "",
            StatusList: [],
            PriorityList: [],
            ShowMyConsultationRequestsDisplaySettings: false,
            SelectedPageSizeOption: 50,
            PageSizeOptions: [50, 100, 150],
            SelectedPageNumber: 1,
            TotalQueryPages: 0,
            TotalQueryRows: 0,
            CurrentPage: 0,
            NewRequestPopupVisible: false,
            OrderByDirection: SortDirection.DESC,
            OrderByColumn: PortfolioConsultationRequestsSortOrderType.CreationDate,
            IsSendToPatientPortalPopupOpen: false,
            PortalLinkedItemModel: null,
            Initialized: false
        };

        const defaultRequestsInboxModel = {
            GlobalPortfolioSearchText: "",
            GlobalPortfolioSearchPlaceholder: "",
            TableData: [],
            Sites: [],
            SelectAllSitesChecked: false,
            SelectAllStatusChecked: false,
            SelectAllCreatedByChecked: false,
            SelectAllRequestingStaffChecked: false,
            SelectAllPriorityChecked: false,
            CreatedByGlobalCache: [],
            CreatedByCliniciansSearchText: "",
            CreatedByClinicianList: [],
            RequestingStaffGlobalCache: [],
            RequestingStaffList: [],
            RequestingStaffsSearchText: "",
            StatusList: [],
            PriorityList: [],
            ShowMyRequestsDisplaySettings: false,
            SelectedPageSizeOption: 50,
            PageSizeOptions: [50, 100, 150],
            SelectedPageNumber: 1,
            TotalQueryPages: 0,
            TotalQueryRows: 0,
            CurrentPage: 0,
            OrderByDirection: SortDirection.DESC,
            OrderByColumn: PortfolioExamRequestSortOrderType.CreatedOn,
            Initialized: false
        };

        const defaultFaxPortfolioModel = {
            GlobalPortfolioSearchText: "",
            GlobalPortfolioSearchPlaceholder: "",
            TableData: [],
            SelectAllSitesChecked: false,
            SelectAllSitesIndeterminate: false,
            Sites: [],
            ShowOnlySelectedSites: false,
            SelectAllCliniciansChecked: false,
            SelectAllCliniciansIndeterminate: false,
            CliniciansSearchText: "",
            CliniciansFiltered: [],
            CliniciansGlobalCache: [],
            SelectAllStatusChecked: true,
            SelectAllStatusIndeterminate: false,
            Status: [],
            IncludeIgnored: true,
            ShowMySentFaxDisplaySettings: false,
            TotalQueryRows: 0,
            CurrentPage: 0,
            PageSizeOptions: [50, 100, 150],
            ColumnOrderingPopupOpen: false,
            SelectedPageSizeOption: 50,
            SelectedPageNumber: 1,
            NewSentFaxPopupVisible: false,
            OrderByDirection: SortDirection.DESC,
            OrderByColumn: PortfolioSentFaxSortOrderType.SentDate,
            DateTo: null,
            DateFrom: null,
            Initialized: false,
            SaveDisplaySettings: false
        };

        const defaultConsultationRequestDataPopupModel = {
            SiteStreetAddress: "",
            SiteZipCode: "",
            SiteState: "",
            SiteName: "",
            SitePhoneNumber: "",
            SiteFax: ""
        };

        const defaultRequestDataPopupModel = {
            SiteStreetAddress: "",
            SiteZipCode: "",
            SiteState: "",
            SiteName: "",
            SitePhoneNumber: "",
            SiteFax: ""
        };

        this.ExpectedResponseStore = {
            LoadPatientConsultationRequest: "",
            LoadPatientRequest: ""
        };

        const defaultPendingModel = {
            TableData: [],
            SelectAllCliniciansChecked: false,
            SelectAllStatusChecked: false,
            DateFrom: null,
            DateTo: null,
            CliniciansSearchText: "",
            CliniciansFiltered: [],
            CliniciansGlobalCache: [],
            Sites: [],
            SelectAllSitesChecked: false,
            ShowPendingApprovalDisplaySettings: false,
            ShowOnlySelectedSites: false,
            Status: [],
            ColumnOrderingPopupOpen: false,
            SearchText: "",
            NoteFileName: "",
            HasNotePDF: false,
            SelectedPendingApprovalEncounterId: "",
            PendingNoteNotesList: [],
            PendingNotesPopup: false,
            ApprovalSupervisorSelectedClinicians: [],
            ApprovalSupervisorClinicianName: "",
            ApprovalSupervisorClinicians: [],
            ApprovalResidentMessage: "",
            SelectedPendingApprovalPatientContactId: "",
            SelectedPendingApprovalSupervisorContactId: "",
            ReturnPendingApprovalMessage: "",
            PdfNoteOpen: true
        };

        const defaultPortalSyncModel = {
            Settings: {
                PageSize: 50,
                PageSizeOptions: [50, 100, 150],
                SearchText: "",
                DateFrom: null as DateTime,
                DateTo: null as DateTime,
                SelectAllSites: false,
                Sites: [],
                SelectAllEmployees: false,
                EmployeeSearchText: "",
                Employees: [],
                SelectAllItemTypes: false,
                ItemTypes: [],
                SelectAllStatuses: false,
                Statuses: [],
                OrderByColumn: PortfolioPortalSyncHistoryOrderType.SyncDate,
                OrderByDirection: SortDirection.DESC
            },
            TotalRows: 0,
            CurrentRowOffset: 0,
            TableData: [],
            SearchFilterPopupOpen: false,
            ColumnOrderPopupOpen: false
        };

        const filteredClinicians: Person[] = [];
        cacheStore.Clinicians.map(x => {
            if (Employee.getIssuingOrgRule(x.EmployeeModel)) {
                const title = Employee.getIssuingOrgRule(x.EmployeeModel).Title;

                if (
                    EmployeeTitleHelper.isDoctor(title)
                    || EmployeeTitleHelper.isNurse(title)
                    || EmployeeTitleHelper.isProfessional(title)
                    || title === EmployeeTitle.Intern
                    || title === EmployeeTitle.Extern
                    || title === EmployeeTitle.Resident
                    || title === EmployeeTitle.Student
                ) {
                    filteredClinicians.push(x);
                }
            }
        });

        defaultPendingModel.ApprovalSupervisorClinicians = filteredClinicians;

        const defaultPreventiveActivitiesInboxModel = {
            PreventiveActivities: [],
            SearchText: "",
            CurrentPage: 1,
            TotalQueryRows: 0,
            SavedSettingsContainerState: PreventiveActivitiesSettingsContainerState.getDefault(),
            OrderByColumn: MyPreventiveActionsSortOrderType.DueDate,
            OrderByDirection: SortDirection.DESC,
            IsOnlyWithinReminderPeriodChecked: false
        };

        this.Model = Immutable.fromJS(defaultModel);
        this.NotesInboxModel = Immutable.fromJS(defaultNotesInboxModel);
        this.ConsultationRequestsInboxModel = Immutable.fromJS(defaultConsultationRequestsInboxModel);
        this.RequestsInboxModel = Immutable.fromJS(defaultRequestsInboxModel);
        this.ConsultationRequestPopupDataModel = Immutable.fromJS(defaultConsultationRequestDataPopupModel);
        this.RequestPopupDataModel = Immutable.fromJS(defaultRequestDataPopupModel);
        this.sentFaxInboxModel = Immutable.fromJS(defaultFaxPortfolioModel);
        this.failedFaxInboxModel = Immutable.fromJS(defaultFaxPortfolioModel);
        this.PendingApprovalInboxModel = Immutable.fromJS(defaultPendingModel);
        this.portalSyncModel = Immutable.fromJS(defaultPortalSyncModel);
        this.preventiveActivitiesInboxModel = Immutable.fromJS(defaultPreventiveActivitiesInboxModel);

        if (cacheStore.ClinicSettings.EnableRecallList) {
            const defaultPreventiveRecallListModel = {
                RecallList: [],
                SearchText: "",
                CurrentPage: 1,
                TotalQueryRows: 0,
                SavedSettingsContainerState: PreventiveRecallListSettingsContainerState.getDefault(),
                CurrentTab: PreventiveRecallTab.Default,
                IsGenerateBonusReportPopupOpen: false,
                IsGenerateBonusReportLoading: false,
                PreventiveCareNotices: []
            };
            this.preventiveRecallListModel = Immutable.fromJS(defaultPreventiveRecallListModel);
        }

        let DefaultApssFilterType = sessionStore.getCurrentEmployee().shouldHaveSupervisorBasedOnTitle() ?
            ApssFilterType.ParCreateur
            : ApssFilterType.ParLicenceMDReferant;
        this.apssInboxModel = Immutable.fromJS(PortfolioHelper.getDefaultApssModel(this.selectedEmployeeAPSS, false, DefaultApssFilterType));

        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => this.serverMapper.Map(response, this));

        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => this.mapper.Map(request, this));

        this.initialLoadDone = false;

        this.IntializePortfolioPopulationBasedFundingFilterSettings();
    }

    public static createSingleton(): PortfolioStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new PortfolioStore());
    }

    public static createSingletonFrom(store: PortfolioStore, copy: boolean = true): PortfolioStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    private GetSiteCheckboxListFromCache(
        checkboxTarget: CheckboxTarget,
        isChecked?: boolean | ((site: SitesModel, i: number, array: SitesModel[]) => boolean),
        filter?: (site: SitesModel, i: number, array: SitesModel[]) => boolean
    ): Immutable.List<SiteUIModel> {
        const sites = filter
            ? cacheStore.SiteCache.filter(filter)
            : cacheStore.SiteCache;

        let checkedFn: (site: SitesModel, i: number, array: SitesModel[]) => boolean;

        if (typeof (isChecked) === "boolean") {
            checkedFn = () => isChecked;
        }
        else if (typeof (isChecked) === "function") {
            checkedFn = isChecked;
        }
        else {
            checkedFn = () => false;
        }

        return Immutable.List(
            sites.map((site, i, array) => {
                let siteUIModel = SiteUIModel.FromCompositeObject(site);
                siteUIModel.Checked = checkedFn(site, i, array);
                siteUIModel.Target = checkboxTarget;

                return siteUIModel;
            })
        );
    }

    private GetEmployeeCheckboxListFromCache(
        checkboxTarget: CheckboxTarget,
        isChecked?: boolean | ((site: Employee, i: number, array: Employee[]) => boolean),
        filter?: (site: Employee, i: number, array: Employee[]) => boolean
    ): Immutable.List<EmployeeUIModel> {
        const employees = filter
            ? cacheStore.EmployeeCacheWithInactive.filter(filter)
            : cacheStore.EmployeeCacheWithInactive;

        let checkedFn: (employee: Employee, i: number, array: Employee[]) => boolean;

        if (typeof (isChecked) === "boolean") {
            checkedFn = () => isChecked;
        }
        else if (typeof (isChecked) === "function") {
            checkedFn = isChecked;
        }
        else {
            checkedFn = () => false;
        }

        return Immutable.List(
            employees.map((employee, i, array) => {
                const employeeUIModel = EmployeeUIModel.fromCompositeObject(employee);
                employeeUIModel.Checked = checkedFn(employee, i, array);
                employeeUIModel.Target = checkboxTarget;

                return employeeUIModel;
            })
        );
    }

    // -------------------------------------------------------------------------
    // Pending Approval Employee List, Site List and Status List ------
    // -------------------------------------------------------------------------

    public LoadPendingApprovalSitesFromCache(): void {
        this.PendingApprovalInboxModel = this.PendingApprovalInboxModel.set("Sites", this.GetSiteCheckboxListFromCache(CheckboxTarget.Myle_Portfolio_PendingApproval_Sites, true));
    }

    public LoadPendingApprovalClinicianUICache(): void {
        const list: EmployeeUIModel[] = [];
        if (EmployeeTitleHelper.isStudent(sessionStore.myleEmployeeTitle)) {
            for (let composite of cacheStore.EmployeeCacheWithInactive) {
                const employee = EmployeeUIModel.fromCompositeObject(composite);
                employee.Target = CheckboxTarget.Myle_Portfolio_PendingApproval_Clinicians;
                list.push(employee);
            }
            this.PendingApprovalInboxModel = this.PendingApprovalInboxModel.set("CliniciansGlobalCache", Immutable.List(list));

            this.PendingApprovalCheckTheClinicianTheMatchesTheCurrentSession();
        }
        else {
            for (let composite of cacheStore.SentForApprovalCliniciansId) {
                let clinician = EmployeeUIModel.fromCompositeObject(cacheStore.GetEmployeeByContactIdOrEmpty(composite));
                clinician.Target = CheckboxTarget.Myle_Portfolio_PendingApproval_Clinicians;
                list.push(clinician);
            }

            this.PendingApprovalInboxModel = this.PendingApprovalInboxModel.set("CliniciansGlobalCache", Immutable.List(list));

            this.PendingApprovalCheckTheClinicianTheMatchesTheCurrentSession();
        }
    }

    public PendingApprovalCheckTheClinicianTheMatchesTheCurrentSession(): void {
        const clinitians = this.PendingApprovalInboxModel.get("CliniciansGlobalCache").toArray().map(cl => {
            cl.Checked = true;
            return cl;
        });

        this.PendingApprovalInboxModel = this.PendingApprovalInboxModel.set("CliniciansFiltered", Immutable.List(clinitians));
    }

    public GetPendingApprovalCliniciansBasedOnSitesAndQuery(): Immutable.List<EmployeeUIModel> {
        let cliniciansFromSites = this.GetPendingApprovalCliniciansFromSelectedSites();
        const filterString = $.trim(this.PendingApprovalInboxModel.get("CliniciansSearchText"));
        if (filterString !== "") { cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites); }
        return cliniciansFromSites;
    }

    public GetPendingApprovalCliniciansFromSelectedSites(): Immutable.List<EmployeeUIModel> {
        const checkedSites = this.helper.GetCheckedItemsFromICheckableList(this.PendingApprovalInboxModel.get("Sites"));

        const allClinicians = this.PendingApprovalInboxModel.get("CliniciansGlobalCache");

        const result: EmployeeUIModel[] = [];
        for (let i = 0; i < checkedSites.size; i++) {
            for (let j = 0; j < allClinicians.size; j++) {
                let clinician = allClinicians.get(j);
                let sites = clinician.Sites;
                let hasSite = sites.filter(item => item === checkedSites.get(i).Id).length > 0;
                if (hasSite) {
                    let toAdd = result.filter(i => i.Id === clinician.Id).length === 0;
                    if (toAdd) { result.push(clinician); } // prevent duplicates due to many-to-many relationship between clinicians and sites
                }
            }
        }
        return Immutable.List(result);
    }

    public InitializePendingApprovalStatus(): void {
        const list = [
            new CheckboxUIModel({
                Id: "status_pending",
                Name: cacheStore.GetString("PendingLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PendingApproval_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: "status_returned",
                Name: cacheStore.GetString("ReturnedLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PendingApproval_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: "status_approved",
                Name: cacheStore.GetString("ApprovedLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PendingApproval_Status,
                Checked: false
            })
        ];

        this.PendingApprovalInboxModel = this.PendingApprovalInboxModel.set("Status", Immutable.List(list));
    }

    // -------------------------------------------------------------------------
    // MY NOTES Employee List, Site List and Status List ------
    // -------------------------------------------------------------------------

    public LoadMyNotesSitesFromCache(): void {
        const a: SiteUIModel[] = [];
        for (let composite of cacheStore.SiteCache) {
            const site = SiteUIModel.FromCompositeObject(composite);
            site.Checked = true;
            site.Target = CheckboxTarget.Myle_Portfolio_MyNotes_Sites;
            a.push(site);
        }

        this.NotesInboxModel = this.NotesInboxModel.set("Sites", Immutable.List(a));
    }

    public LoadMyNotesClinicianUICache(): void {
        const list: EmployeeUIModel[] = [];
        for (let composite of cacheStore.EmployeeCacheWithInactive) {
            const employee = EmployeeUIModel.fromCompositeObject(composite);
            employee.Target = CheckboxTarget.Myle_Portfolio_MyNotes_Clinicians;
            list.push(employee);
        }

        this.NotesInboxModel = this.NotesInboxModel.set("CliniciansGlobalCache", Immutable.List(list));

        this.MyNotesCheckTheClinicianTheMatchesTheCurrentSession();
    }

    public MyNotesCheckTheClinicianTheMatchesTheCurrentSession(): void {
        let clinicians = this.GetMyNotesCliniciansBasedOnSitesAndQuery();
        clinicians = clinicians.toArray().map(
            cl => {
                if (cl.Id === sessionStore.individualContactId) { cl.Checked = true; }
                return cl;
            }
        ).toImmutable();

        this.NotesInboxModel = this.NotesInboxModel.set("CliniciansFiltered", clinicians);
    }

    public GetMyNotesCliniciansBasedOnSitesAndQuery(): Immutable.List<EmployeeUIModel> {
        let cliniciansFromSites = this.GetMyNotesCliniciansFromSelectedSites();
        const filterString = $.trim(this.NotesInboxModel.get("CliniciansSearchText"));
        if (filterString !== "") { cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites); }
        return cliniciansFromSites;
    }

    public GetMyNotesCliniciansFromSelectedSites(): Immutable.List<EmployeeUIModel> {
        const checkedSites = this.helper.GetCheckedItemsFromICheckableList(this.NotesInboxModel.get("Sites"));

        const allClinicians = this.NotesInboxModel.get("CliniciansGlobalCache");

        const result: EmployeeUIModel[] = [];
        for (let i = 0; i < checkedSites.size; i++) {
            for (let j = 0; j < allClinicians.size; j++) {
                const clinician = allClinicians.get(j);
                const sites = clinician.Sites;
                const hasSite = sites.filter(item => item === checkedSites.get(i).Id).length > 0;
                if (hasSite) {
                    const toAdd = result.filter(i => i.Id === clinician.Id).length === 0;
                    if (toAdd) { result.push(clinician); } // prevent duplicates due to many-to-many relationship between clinicians and sites
                }
            }
        }
        return Immutable.List(result);
    }

    public InitializeMyNotesStatus(): void {
        const list = [
            new CheckboxUIModel({
                Id: "status_approved",
                Name: cacheStore.GetString("ApprovedResidentLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MyNotes_Status
            }),
            new CheckboxUIModel({
                Id: "status_not_approved",
                Name: cacheStore.GetString("UnapprovedResidentLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MyNotes_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: "status_send_for_approval",
                Name: cacheStore.GetString("PendingResidentLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MyNotes_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: "status_returned",
                Name: cacheStore.GetString("ReturnedResidentLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MyNotes_Status
            })
        ];

        this.NotesInboxModel = this.NotesInboxModel.set("Status", Immutable.List(list));
    }

    // -------------------------------------------------------------------------
    // My Consultation and Exam Requests status, priorities, employee and site lists.
    // -------------------------------------------------------------------------

    public getInitializedMedicalRequestInboxSettings(
        model: ILabRequestsInboxModel | IConsultationRequestInboxModel,
        settings: MedicalRequestsInboxSettings,
        dropDownKey: string
    ): ILabRequestsInboxModel | IConsultationRequestInboxModel {
        if (!model.get("Initialized")) {
            model = model
                .set("Sites", this.getRequestsSitesFromCache())
                .set("PriorityList", this.getMedicalRequestsPriorities(dropDownKey))
                .set("StatusList", MedicalRequestStatus.getPortfolioCheckboxOptions())
                .set("RequestingStaffGlobalCache", this.getRequestsClinicianUICache())
                .set("RequestingStaffList", this.getRequestsClinicianUICache())
                .set("CreatedByGlobalCache", this.getRequestsClinicianUICache())
                .set("CreatedByClinicianList", this.getRequestsClinicianUICache());

            model = this.loadDefaultSettings(model, settings)
                .set("Initialized", true);
        }

        return model;
    }

    private loadDefaultSettings(model: ILabRequestsInboxModel | IConsultationRequestInboxModel, settings: MedicalRequestsInboxSettings): ILabRequestsInboxModel | IConsultationRequestInboxModel {
        if (!settings)
            return model;

        return model
            .set("SelectAllPriorityChecked", settings.IsAllPriorityChecked ?? model.get("SelectAllPriorityChecked"))
            .set("SelectAllCreatedByChecked", settings.IsAllCreatedByClinicianChecked ?? model.get("SelectAllCreatedByChecked"))
            .set("SelectAllSitesChecked", settings.IsAllSitesChecked ?? model.get("SelectAllSitesChecked"))
            .set("SelectAllRequestingStaffChecked", settings.IsAllRequestingStaffChecked ?? model.get("SelectAllRequestingStaffChecked"))
            .set("SelectAllStatusChecked", settings.IsAllStatusChecked ?? model.get("SelectAllStatusChecked"))
            .set("Sites", model.get("Sites").mutate(s => s.Checked = settings.SiteIds.contains(s.Id)))
            .set("CreatedByGlobalCache", model.get("CreatedByGlobalCache").mutate(e => e.Checked = settings.CreatedByCliniciansIds.contains(e.Model.ContactFacet_id)))
            .set("RequestingStaffGlobalCache", model.get("RequestingStaffGlobalCache").mutate(e => e.Checked = settings.RequestingStaffIds.contains(e.Model.ContactFacet_id)))
            .set("CreatedByClinicianList", model.get("CreatedByGlobalCache").mutate(e => e.Checked = settings.CreatedByCliniciansIds.contains(e.Model.ContactFacet_id)))
            .set("RequestingStaffList", model.get("RequestingStaffGlobalCache").mutate(e => e.Checked = settings.RequestingStaffIds.contains(e.Model.ContactFacet_id)))
            .set("StatusList", model.get("StatusList").mutate(e => e.Checked = settings.Statuses.contains(e.Id)))
            .set("PriorityList", model.get("PriorityList").mutate(e => e.Checked = settings.Priorities.contains(e.Id)))
            .set("SelectedPageSizeOption", settings.PageSize ?? model.get("SelectedPageSizeOption"))
            .set("Initialized", true);
    }

    public getRequestsSitesFromCache(): Immutable.List<SiteUIModel> {
        return (cacheStore.SiteCache ?? [])
            .map(site => {
                const siteUi = SiteUIModel.FromCompositeObject(site);
                siteUi.Checked = true;
                return siteUi;
            })
            .toImmutable();
    }

    public getRequestsClinicianUICache(): Immutable.List<EmployeeUIModel> {
        return (cacheStore.EmployeeCacheWithInactive ?? [])
            .map(employee => {
                const emp = EmployeeUIModel.fromCompositeObject(employee);
                if (emp.Id === sessionStore.individualContactId)
                    emp.Checked = true;
                return emp;
            })
            .toImmutable();
    }

    public getMedicalRequestsPriorities(dropDownKey: string): Immutable.List<CheckboxUIModel> {
        const prioritiesDictionary = cacheStore.getDropDownOptionByType(dropDownKey);

        const urgent = prioritiesDictionary.firstOrDefault(x => x.model.Code === "Urgent");
        const normal = prioritiesDictionary.firstOrDefault(x => x.model.Code === "Normal");
        const semiUrgent = prioritiesDictionary.firstOrDefault(x => x.model.Code === "SemiUrgent");
        const elective = prioritiesDictionary.firstOrDefault(x => x.model.Code === "Elective");

        return [normal, semiUrgent, urgent, elective]
            .map(p => new CheckboxUIModel({
                Id: p.model.Id,
                Name: p.model.VocabularyResourceKey,
                Checked: true
            }))
            .toImmutable();
    }

    // -------------------------------------------------------------------------
    // MY SENT FAX Employee List, Site List and Status list
    // -------------------------------------------------------------------------

    public getActiveFaxModel(): ISentFaxInboxModel {
        return this.Model.get("CurrentInbox") == PortfolioInboxes.FailedFaxes
            ? this.failedFaxInboxModel
            : this.sentFaxInboxModel;
    }

    public setActiveFaxModel(model: ISentFaxInboxModel): void {
        if (this.Model.get("CurrentInbox") == PortfolioInboxes.FailedFaxes)
            this.failedFaxInboxModel = model;
        else
            this.sentFaxInboxModel = model;
    }

    public initializeFaxPortfolioInbox(inbox: PortfolioInboxes): void {
        let model = this.getActiveFaxModel();

        model = this.loadMySentFaxSitesFromCache(model);
        model = this.loadMySentFaxClinicianUICache(model);
        model = model.set("Status", this.initializeMySentFaxStatus(inbox).toImmutable())
            .set("IncludeIgnored", inbox == PortfolioInboxes.Faxes)
            .set("DateFrom", moment().subtract(6, "months").toDate())
            .set("DateTo", moment().toDate());

        model = model.set("Initialized", true);

        const settings = this.Model.get("CurrentInbox") == PortfolioInboxes.Faxes
            ? cacheStore.UserSettings?.PortfolioSentFaxSearchSettings
            : cacheStore.UserSettings?.PortfolioFailedFaxesFilterSetting;

        if (settings) {
            const sites = model.get("Sites").map(site => ({ ...site, Checked: settings.SiteIds ? settings.SiteIds?.contains(site.Id) : true }));
            model = model.set("Sites", Immutable.List(sites));
            const cliniciansFiltered = this.getMySentFaxCliniciansBasedOnSitesAndQuery(model);

            cliniciansFiltered.forEach(clinician => {
                if (settings.ClinicianIds.contains(clinician.Id))
                    clinician.Checked = true;
            });
            const status = model.get("Status").map(status => ({ ...status, Checked: settings.Statuses ? settings.Statuses?.contains(status.Id) : true }));
            const sitesState = IndeterminateCheckboxState.getAllCheckboxStates(Immutable.List(sites));
            const cliniciansState = IndeterminateCheckboxState.getAllCheckboxStates(Immutable.List(cliniciansFiltered));
            const statusesState = IndeterminateCheckboxState.getAllCheckboxStates(Immutable.List(status));

            model = model.set("SelectAllSitesChecked", sitesState.allChecked)
                .set("SelectAllSitesIndeterminate", !sitesState.allChecked && !sitesState.noneChecked)
                .set("CliniciansFiltered", Immutable.List(cliniciansFiltered))
                .set("SelectAllCliniciansChecked", settings.ShowAllClinicians ?? false)
                .set("SelectAllCliniciansIndeterminate", !cliniciansState.allChecked && !cliniciansState.noneChecked)
                .set("Status", Immutable.List(status))
                .set("SelectAllStatusChecked", settings.ShowAllStatuses ?? false)
                .set("SelectAllStatusIndeterminate", !statusesState.allChecked && !statusesState.noneChecked)
                .set("ShowOnlySelectedSites", settings.ShowOnlySelectedSites ?? false)
                .set("IncludeIgnored", settings.IncludeIgnored ?? false)
                .set("SelectedPageSizeOption", settings.SelectedPageSizeOption || this.sentFaxInboxModel.get("SelectedPageSizeOption"));
        }
        else {
            model = this.setCheckStateToClinician(sessionStore.individualContactId, true, model);
        }

        this.setActiveFaxModel(model);
        this.PortfolioActions.LoadPortfolioFaxes(model);
    }

    public getMySentFaxCliniciansBasedOnSitesAndQuery(model: ISentFaxInboxModel): Immutable.List<EmployeeUIModel> {
        let cliniciansFromSites = this.getMySentFaxCliniciansFromSelectedSites(model);
        const filterString = $.trim(model.get("CliniciansSearchText"));
        if (filterString !== "") {
            cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites);
        }
        return cliniciansFromSites;
    }

    private getMySentFaxCliniciansFromSelectedSites(model: ISentFaxInboxModel): Immutable.List<EmployeeUIModel> {
        const checkedSiteIds = this.helper.GetCheckedItemsFromICheckableList(model.get("Sites")).map((s: SiteUIModel) => s.Id).toArray();
        const allClinicians = model.get("CliniciansGlobalCache");
        return SearchMenuHelper.FilterCliniciansMembersOfSites(allClinicians, checkedSiteIds);
    }

    private setCheckStateToClinician(id: string, checked: boolean, model: ISentFaxInboxModel): ISentFaxInboxModel {
        const clinitians = model.get("CliniciansGlobalCache").map(cl => {
            if (cl.Id === id) {
                cl.Checked = true;
            }
            return cl;
        });

        return model.set("CliniciansFiltered", clinitians.toList());
    }

    private initializeMySentFaxStatus(inboxType: PortfolioInboxes): CheckboxUIModel[] {
        let isFailedFaxView = inboxType == PortfolioInboxes.FailedFaxes;

        return [
            new CheckboxUIModel({
                Id: PortfolioSentFaxStatus.InProgress + "",
                Name: cacheStore.GetString("InProgressLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                Checked: !isFailedFaxView
            }),
            new CheckboxUIModel({
                Id: PortfolioSentFaxStatus.Sent + "",
                Name: cacheStore.GetString("SentLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                Checked: !isFailedFaxView
            }),
            new CheckboxUIModel({
                Id: PortfolioSentFaxStatus.Failed + "",
                Name: cacheStore.GetString("FailedLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: PortfolioSentFaxStatus.NoAnswer + "",
                Name: cacheStore.GetString("NoAnswerLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: PortfolioSentFaxStatus.Busy + "",
                Name: cacheStore.GetString("BusyLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: PortfolioSentFaxStatus.NoRemoteFax + "",
                Name: cacheStore.GetString("NoRemoteFax"),
                Target: CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: PortfolioSentFaxStatus.NotSent + "",
                Name: cacheStore.GetString("NotSentLabel"),
                Target: CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: PortfolioSentFaxStatus.Queued + "",
                Name: cacheStore.GetString("Enums_SentFaxStatus_Queued"),
                Target: CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                Checked: true
            })
        ];
    }

    private loadMySentFaxSitesFromCache(model: ISentFaxInboxModel): ISentFaxInboxModel {
        const a = cacheStore.SiteCache.map(s => {
            const site = SiteUIModel.FromCompositeObject(s);
            site.Checked = true;
            site.Target = CheckboxTarget.Myle_Portfolio_MySentFaxes_Sites;
            return site;
        });

        return model.set("Sites", Immutable.List(a));
    }

    private loadMySentFaxClinicianUICache(model: ISentFaxInboxModel): ISentFaxInboxModel {
        const list = cacheStore.EmployeeCacheWithInactive.map(e => {
            const employee = EmployeeUIModel.fromCompositeObject(e);
            employee.Checked = false;
            employee.Target = CheckboxTarget.Myle_Portfolio_MySentFaxes_Clinicians;
            return employee;
        });

        return model.set("CliniciansGlobalCache", Immutable.List(list));
    }

    // -------------------------------------------------------------------------
    // Portal Sync History Employee List, Site List and Status list
    // -------------------------------------------------------------------------

    public initializePortalSyncModel(): void {
        const settings = this.portalSyncModel.get("Settings")
            .set("Sites", this.GetSiteCheckboxListFromCache(CheckboxTarget.Myle_Portfolio_PortalSyncHistory_Sites))
            .set("Employees", this.GetEmployeeCheckboxListFromCache(CheckboxTarget.Myle_Portfolio_PortalSyncHistory_Users, p => p.Contact_id === sessionStore.individualContactId))
            .set("ItemTypes", this.getPortalSyncItemTypes())
            .set("Statuses", this.getPortalSyncStatuses());

        this.portalSyncModel = this.portalSyncModel
            .set("Initialized", true)
            .set("Settings", settings);
    }

    private getPortalSyncItemTypes(): Immutable.List<CheckboxUIModel> {
        return Immutable.List([
            new CheckboxUIModel({
                Id: `PortalSyncItemType_${PortalLinkedItemType.Document}`,
                Name: cacheStore.GetString("DocumentsLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_ItemTypes
            }),
            new CheckboxUIModel({
                Id: `PortalSyncItemType_${PortalLinkedItemType.Result}`,
                Name: cacheStore.GetString("ResultsLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_ItemTypes
            }),
            new CheckboxUIModel({
                Id: `PortalSyncItemType_${PortalLinkedItemType.PrivateBill}`,
                Name: cacheStore.GetString("InvoicesLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_ItemTypes
            }),
            new CheckboxUIModel({
                Id: `PortalSyncItemType_${PortalLinkedItemType.CommunicationHistory}`,
                Name: cacheStore.GetString("MessagesLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_ItemTypes
            }),
            new CheckboxUIModel({
                Id: `PortalSyncItemType_${PortalLinkedItemType.LabRequest}`,
                Name: cacheStore.GetString("ExamRequestsLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_ItemTypes
            }),
            new CheckboxUIModel({
                Id: `PortalSyncItemType_${PortalLinkedItemType.ConsultationRequest}`,
                Name: cacheStore.GetString("ConsultationRequestsLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_ItemTypes
            }),
            new CheckboxUIModel({
                Id: `PortalSyncItemType_${PortalLinkedItemType.MedicalNoteForm}`,
                Name: cacheStore.GetString("FormLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_ItemTypes
            })
        ]);
    }

    private getPortalSyncStatuses(): Immutable.List<CheckboxUIModel> {
        return Immutable.List([
            new CheckboxUIModel({
                Id: `EnumPortalSyncStatus_${EnumPortalSyncStatus.pending}`,
                Name: cacheStore.GetString("PendingLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_Statuses,
                Checked: true
            }),
            new CheckboxUIModel({
                Id: `EnumPortalSyncStatus_${EnumPortalSyncStatus.viewed}`,
                Name: cacheStore.GetString("ViewedLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_Statuses
            }),
            new CheckboxUIModel({
                Id: `EnumPortalSyncStatus_${EnumPortalSyncStatus.revoked}`,
                Name: cacheStore.GetString("RevokedLabel"),
                Target: CheckboxTarget.Myle_Portfolio_PortalSyncHistory_Statuses
            })
        ]);
    }

    // -------------------------------------------------------------------------
    // Population Based Funding, Patient Register Status list and Proposal list
    // -------------------------------------------------------------------------

    private IntializePortfolioPopulationBasedFundingFilterSettings(): void {
        const model = { ...this.populationBasedFundingPortfolioModel };
        model.AvailableSites = this.getRequestsSitesFromCache().map(value => value).toJS()
            .sort((siteA: SiteUIModel, siteB: SiteUIModel) => siteA.Name.localeCompare(siteB.Name));
        model.AvailableClinicians = this.getRequestsClinicianUICache().mutate(clinician => clinician.Checked = true).toJS()
            .sort((clinicianA: EmployeeUIModel, clinicianB: EmployeeUIModel) => clinicianA.Name.localeCompare(clinicianB.Name));
        model.AvailableStatus = this.GetPopulationBasedFundingStatuses();

        if (cacheStore.UserSettings.PortfolioPopulationBasedFundingFilter) {
            model.FilterSettings = { ...cacheStore.UserSettings.PortfolioPopulationBasedFundingFilter };
        }
        else {
            const defaultSettings = this.GetDefaultPortfolioPopulationBasedFundingFilterSettings(model);
            model.FilterSettings = defaultSettings;
        }

        this.populationBasedFundingPortfolioModel = model;
    }

    private GetPopulationBasedFundingStatuses(): PopulationBasedFundingStatusUIModel[] {
        return [
            new PopulationBasedFundingStatusUIModel(PopulationBasedFundingStatus.Registered),
            new PopulationBasedFundingStatusUIModel(PopulationBasedFundingStatus.Deregistered),
            new PopulationBasedFundingStatusUIModel(PopulationBasedFundingStatus.ProposedRegistration),
            new PopulationBasedFundingStatusUIModel(PopulationBasedFundingStatus.ProposedDeregistration),
            new PopulationBasedFundingStatusUIModel(PopulationBasedFundingStatus.InternalProposedRegistration)
        ];
    }

    private GetDefaultPortfolioPopulationBasedFundingFilterSettings(model: PopulationBasedFundingPortfolioModel): PopulationBasedFundingPortfolioFilterSettings {
        const defaultFilterSettings = new PopulationBasedFundingPortfolioFilterSettings();

        defaultFilterSettings.IncludedStatuses = model.AvailableStatus.map(status => status.Enum);
        defaultFilterSettings.IncludedSites = model.AvailableSites.map(site => site.Id);
        defaultFilterSettings.IncludedEmployeeContactFacetIds = model.AvailableClinicians.map(clinician => clinician.ContactFacetId);

        return defaultFilterSettings;
    }
}

export { PortfolioStore, defaultInstance as portfolioStore };
